import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import Tree from 'react-d3-tree';

// Styled Components
const PageContainer = styled.div`
  display: flex;
  height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const LeftPanel = styled.div`
  width: 350px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  padding: 20px;
  overflow-y: auto;
`;

const RightPanel = styled.div`
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h1`
  color: white;
  text-align: center;
  margin-bottom: 30px;
  font-size: 2.5rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const Controls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ControlGroup = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Label = styled.label`
  display: block;
  color: white;
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 5px;
  background: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  margin-bottom: 10px;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 5px;
  background: ${props => {
    if (props.$variant === 'danger') return '#ff6b6b';
    if (props.$variant === 'warning') return '#ffd43b';
    return '#4ecdc4';
  }};
  color: white;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 8px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const VisualizationContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 30px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  min-width: 1000px;
  min-height: 700px;
  position: relative;
  overflow: hidden;
  
  /* Custom tree link styling */
  .tree-link {
    stroke: white !important;
    stroke-width: 2px !important;
    fill: none !important;
  }
  
  /* Remove default D3 tree background */
  .rd3t-tree-container {
    background: transparent !important;
  }
`;





const TreeInfo = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-top: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
`;

const InfoText = styled.p`
  color: white;
  margin: 5px 0;
  font-size: 0.9rem;
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 15px;
`;

const StatBox = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px;
  text-align: center;
  flex: 1;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const StatValue = styled.div`
  color: #4ecdc4;
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  color: white;
  font-size: 0.8rem;
  opacity: 0.8;
`;

const OperationLog = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-top: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  max-height: 150px;
  overflow-y: auto;
`;

const LogTitle = styled.h3`
  color: white;
  margin-bottom: 10px;
  font-size: 1rem;
`;

const LogEntry = styled.div`
  color: white;
  font-size: 0.8rem;
  margin: 3px 0;
  padding: 5px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 5px;
`;

const TraversalResult = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 15px;
  margin-top: 15px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
`;

const TraversalTitle = styled.h4`
  color: white;
  margin-bottom: 10px;
  font-size: 1rem;
`;

const TraversalPath = styled.div`
  color: #4ecdc4;
  font-family: monospace;
  font-size: 1.1rem;
  background: rgba(0, 0, 0, 0.2);
  padding: 10px;
  border-radius: 5px;
  text-align: center;
`;

// Binary Tree Node Class
class BinaryTreeNode {
  constructor(value) {
    this.value = value;
    this.left = null;
    this.right = null;
  }
}

// Binary Tree Class
class BinaryTree {
  constructor() {
    this.root = null;
    this.size = 0;
  }

  insert(value) {
    const newNode = new BinaryTreeNode(value);
    
    if (!this.root) {
      this.root = newNode;
      this.size = 1;
      return;
    }

    const queue = [this.root];
    
    while (queue.length > 0) {
      const current = queue.shift();
      
      if (!current.left) {
        current.left = newNode;
        this.size++;
        return;
      }
      
      if (!current.right) {
        current.right = newNode;
        this.size++;
        return;
      }
      
      queue.push(current.left);
      queue.push(current.right);
    }
  }

  delete(value) {
    if (!this.root) return false;
    
    if (this.root.value === value) {
      if (!this.root.left && !this.root.right) {
        this.root = null;
        this.size = 0;
        return true;
      }
      
      if (!this.root.left) {
        this.root = this.root.right;
        this.size--;
        return true;
      }
      
      if (!this.root.right) {
        this.root = this.root.left;
        this.size--;
        return true;
      }
      
      // Find the rightmost node in the left subtree
      let current = this.root.left;
      while (current.right) {
        current = current.right;
      }
      
      this.root.value = current.value;
      this.deleteNode(this.root.left, current.value);
      this.size--;
      return true;
    }
    
    return this.deleteNode(this.root, value);
  }

  deleteNode(node, value) {
    if (!node) return false;
    
    if (node.left && node.left.value === value) {
      if (!node.left.left && !node.left.right) {
        node.left = null;
        this.size--;
        return true;
      }
      
      if (!node.left.left) {
        node.left = node.left.right;
        this.size--;
        return true;
      }
      
      if (!node.left.right) {
        node.left = node.left.left;
        this.size--;
        return true;
      }
      
      let current = node.left.left;
      while (current.right) {
        current = current.right;
      }
      
      node.left.value = current.value;
      this.deleteNode(node.left.left, current.value);
      this.size--;
      return true;
    }
    
    if (node.right && node.right.value === value) {
      if (!node.right.left && !node.right.right) {
        node.right = null;
        this.size--;
        return true;
      }
      
      if (!node.right.left) {
        node.right = node.right.right;
        this.size--;
        return true;
      }
      
      if (!node.right.right) {
        node.right = node.right.left;
        this.size--;
        return true;
      }
      
      let current = node.right.left;
      while (current.right) {
        current = current.right;
      }
      
      node.right.value = current.value;
      this.deleteNode(node.right.left, current.value);
      this.size--;
      return true;
    }
    
    return this.deleteNode(node.left, value) || this.deleteNode(node.right, value);
  }

  search(value) {
    return this.searchNode(this.root, value);
  }

  searchNode(node, value) {
    if (!node) return false;
    if (node.value === value) return true;
    return this.searchNode(node.left, value) || this.searchNode(node.right, value);
  }

  getHeight() {
    return this.getNodeHeight(this.root);
  }

  getNodeHeight(node) {
    if (!node) return 0;
    return Math.max(this.getNodeHeight(node.left), this.getNodeHeight(node.right)) + 1;
  }

  getLevelOrder() {
    if (!this.root) return [];
    
    const result = [];
    const queue = [this.root];
    
    while (queue.length > 0) {
      const current = queue.shift();
      result.push(current.value);
      
      if (current.left) queue.push(current.left);
      if (current.right) queue.push(current.right);
    }
    
    return result;
  }

  getInOrder() {
    const result = [];
    this.inOrderTraversal(this.root, result);
    return result;
  }

  inOrderTraversal(node, result) {
    if (node) {
      this.inOrderTraversal(node.left, result);
      result.push(node.value);
      this.inOrderTraversal(node.right, result);
    }
  }

  getPreOrder() {
    const result = [];
    this.preOrderTraversal(this.root, result);
    return result;
  }

  preOrderTraversal(node, result) {
    if (node) {
      result.push(node.value);
      this.preOrderTraversal(node.left, result);
      this.preOrderTraversal(node.right, result);
    }
  }

  getPostOrder() {
    const result = [];
    this.postOrderTraversal(this.root, result);
    return result;
  }

  postOrderTraversal(node, result) {
    if (node) {
      this.postOrderTraversal(node.left, result);
      this.postOrderTraversal(node.right, result);
      result.push(node.value);
    }
  }

  countNodes() {
    return this.countNodesRecursive(this.root);
  }

  countNodesRecursive(node) {
    if (!node) return 0;
    return 1 + this.countNodesRecursive(node.left) + this.countNodesRecursive(node.right);
  }

  printTree() {
    const result = [];
    this.printTreeRecursive(this.root, result, 0);
    return result;
  }

  printTreeRecursive(node, result, level) {
    if (!node) return;
    
    result.push(`${'  '.repeat(level)}${node.value}`);
    this.printTreeRecursive(node.left, result, level + 1);
    this.printTreeRecursive(node.right, result, level + 1);
  }

  copyTree(node) {
    if (!node) return null;
    
    const newNode = new BinaryTreeNode(node.value);
    newNode.left = this.copyTree(node.left);
    newNode.right = this.copyTree(node.right);
    
    return newNode;
  }

  hasCircularReference() {
    const visited = new Set();
    return this.checkCircular(this.root, visited);
  }

  checkCircular(node, visited) {
    if (!node) return false;
    
    if (visited.has(node)) return true;
    visited.add(node);
    
    return this.checkCircular(node.left, visited) || this.checkCircular(node.right, visited);
  }
}

function TreePage() {
  const [tree, setTree] = useState(new BinaryTree());
  const [inputValue, setInputValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [highlightedNode, setHighlightedNode] = useState(null);
  const [visitedNodes, setVisitedNodes] = useState(new Set());
  const [currentNode, setCurrentNode] = useState(null);
  const [operationLog, setOperationLog] = useState([]);
  const [traversalResult, setTraversalResult] = useState(null);
  const [animationSpeed, setAnimationSpeed] = useState(50);
  const [isOperating, setIsOperating] = useState(false);
  const [fastMode, setFastMode] = useState(false);

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const addToLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setOperationLog(prev => [...prev.slice(-9), `${timestamp}: ${message}`]);
  };

  const insertNode = async () => {
    if (!inputValue.trim()) return;
    
    const value = parseInt(inputValue.trim());
    if (isNaN(value)) {
      addToLog('Please enter a valid number');
      return;
    }
    
    if (tree.search(value)) {
      addToLog(`Value ${value} already exists in the tree`);
      return;
    }
    
    setIsOperating(true);
    setCurrentNode(value);
    addToLog(`Inserting ${value} into the tree`);
    
    await delay(animationSpeed);
    
    const newTree = new BinaryTree();
    newTree.root = tree.copyTree(tree.root);
    newTree.size = tree.size;
    newTree.insert(value);
    
    setTree(newTree);
    setCurrentNode(null);
    setIsOperating(false);
    addToLog(`Successfully inserted ${value}`);
    addToLog(`New tree size: ${newTree.size}, Actual nodes: ${newTree.countNodes()}`);
    setInputValue('');
  };

  const deleteNode = async () => {
    if (!inputValue.trim()) return;
    
    const value = parseInt(inputValue.trim());
    if (isNaN(value)) {
      addToLog('Please enter a valid number');
      return;
    }
    
    if (!tree.search(value)) {
      addToLog(`Value ${value} not found in the tree`);
      return;
    }
    
    setIsOperating(true);
    setCurrentNode(value);
    addToLog(`Deleting ${value} from the tree`);
    
    await delay(animationSpeed);
    
    const newTree = new BinaryTree();
    newTree.root = tree.copyTree(tree.root);
    newTree.size = tree.size;
    newTree.delete(value);
    
    setTree(newTree);
    setCurrentNode(null);
    setIsOperating(false);
    addToLog(`Successfully deleted ${value}`);
    setInputValue('');
  };

  const searchNode = async () => {
    if (!searchValue.trim()) return;
    
    const value = parseInt(searchValue.trim());
    if (isNaN(value)) {
      addToLog('Please enter a valid number');
      return;
    }
    
    setIsOperating(true);
    setCurrentNode(value);
    addToLog(`Searching for ${value}`);
    
    await delay(animationSpeed);
    
    const found = tree.search(value);
    setCurrentNode(null);
    setIsOperating(false);
    
    if (found) {
      addToLog(`Found ${value} in the tree`);
      setHighlightedNode(value);
      setTimeout(() => setHighlightedNode(null), 2000);
    } else {
      addToLog(`${value} not found in the tree`);
    }
  };

  const performTraversal = async (type) => {
    if (!tree.root) {
      addToLog('Tree is empty - no traversal possible');
      return;
    }
    
    setIsOperating(true);
    addToLog(`Performing ${type} traversal`);
    addToLog(`Tree size: ${tree.size}`);
    addToLog(`Actual node count: ${tree.countNodes()}`);
    
    // Check for circular references that could cause infinite loops
    if (tree.hasCircularReference()) {
      addToLog('Error: Tree contains circular references - traversal will hang');
      setIsOperating(false);
      return;
    }
    
    // Add timeout to prevent hanging
    const timeoutId = setTimeout(() => {
      addToLog('Error: Traversal timeout - operation taking too long');
      setIsOperating(false);
    }, 10000); // 10 second timeout - more reasonable
    
    let result;
    switch (type) {
      case 'level':
        result = tree.getLevelOrder();
        break;
      case 'inorder':
        result = tree.getInOrder();
        break;
      case 'preorder':
        result = tree.getPreOrder();
        break;
      case 'postorder':
        result = tree.getPostOrder();
        break;
      default:
        result = [];
    }
    
    addToLog(`Traversal result length: ${result.length}`);
    addToLog(`Result: [${result.join(', ')}]`);
    
    // Validate tree structure
    if (result.length === 0) {
      addToLog('Error: Traversal returned empty result - tree structure may be corrupted');
      clearTimeout(timeoutId);
      setIsOperating(false);
      return;
    }
    
    if (result.length !== tree.countNodes()) {
      addToLog(`Warning: Traversal result (${result.length}) doesn't match actual node count (${tree.countNodes()})`);
    }
    
    // Animate through the traversal
    setVisitedNodes(new Set());
    const startTime = Date.now();
    
    if (fastMode) {
      // Fast mode: show all nodes at once without animation
      setVisitedNodes(new Set(result));
      addToLog(`Fast mode: All ${result.length} nodes visited instantly`);
    } else {
      // Normal mode: animate through each node with safety checks
      for (let i = 0; i < result.length; i++) {
        try {
          // Safety check: ensure we don't get stuck
          if (i > result.length * 2) {
            addToLog(`Error: Traversal stuck at node ${i}, aborting`);
            break;
          }
          
          setVisitedNodes(prev => new Set([...prev, result[i]]));
          
          // Log progress more frequently for debugging
          if (i % 3 === 0 || i === result.length - 1) {
            addToLog(`Visiting node ${i + 1}/${result.length}: ${result[i]}`);
          }
          
          // Use a much shorter delay to prevent getting stuck
          await delay(50); // Fixed 50ms delay instead of variable
          
          // Add progress indicator
          if (i % 2 === 0) {
            addToLog(`Progress: ${Math.round((i / result.length) * 100)}% complete`);
          }
          
          // Auto-switch to fast mode if taking too long
          if (i > 0 && i % 5 === 0) {
            const elapsed = Date.now() - startTime;
            if (elapsed > 5000) { // If taking more than 5 seconds
              addToLog(`Switching to fast mode due to slow progress`);
              setVisitedNodes(new Set(result));
              break;
            }
          }
        } catch (error) {
          addToLog(`Error at node ${i + 1}: ${error.message}`);
          break;
        }
      }
    }
    
    clearTimeout(timeoutId);
    setTraversalResult({ type, path: result });
    setIsOperating(false);
    addToLog(`${type.charAt(0).toUpperCase() + type.slice(1)} traversal completed`);
  };

  const clearTree = () => {
    if (!tree.root) {
      addToLog('Tree is already empty');
      return;
    }
    
    setTree(new BinaryTree());
    setVisitedNodes(new Set());
    setCurrentNode(null);
    setHighlightedNode(null);
    setTraversalResult(null);
    addToLog('Tree cleared');
  };

  const generateRandomTree = () => {
    const newTree = new BinaryTree();
    const numNodes = Math.floor(Math.random() * 15) + 5; // 5-19 nodes
    
    for (let i = 0; i < numNodes; i++) {
      const value = Math.floor(Math.random() * 100);
      if (!newTree.search(value)) {
        newTree.insert(value);
      }
    }
    
    setTree(newTree);
    setVisitedNodes(new Set());
    setCurrentNode(null);
    setHighlightedNode(null);
    setTraversalResult(null);
    addToLog(`Generated random tree with ${newTree.size} nodes`);
  };

  // Convert binary tree to D3 tree format
  const convertToD3Format = useCallback((node) => {
    if (!node) return null;
    
    const d3Node = {
      name: node.value.toString(),
      attributes: {
        value: node.value,
        isHighlighted: highlightedNode === node.value,
        isVisited: visitedNodes.has(node.value),
        isCurrent: currentNode === node.value,
      },
      children: []
    };
    
    if (node.left) {
      d3Node.children.push(convertToD3Format(node.left));
    }
    if (node.right) {
      d3Node.children.push(convertToD3Format(node.right));
    }
    
    return d3Node;
  }, [highlightedNode, visitedNodes, currentNode]);

  // Get D3 tree data
  const treeData = tree.root ? convertToD3Format(tree.root) : null;

  // Custom node styling
  const renderCustomNode = ({ nodeDatum, toggleNode }) => {
    const isHighlighted = nodeDatum.attributes?.isHighlighted;
    const isVisited = nodeDatum.attributes?.isVisited;
    const isCurrent = nodeDatum.attributes?.isCurrent;
    
    let backgroundColor = '#ffffff';
    if (isHighlighted) backgroundColor = '#ffd43b';
    else if (isVisited) backgroundColor = '#4ecdc4';
    else if (isCurrent) backgroundColor = '#51cf66';
    
    return (
      <g>
        <circle
          r={55}
          fill={backgroundColor}
          stroke="white"
          strokeWidth="5"
          style={{ cursor: 'pointer' }}
          onClick={toggleNode}
        />
        <text
          textAnchor="middle"
          dy=".35em"
          style={{ 
            fontSize: '28px', 
            fontWeight: '900', 
            fill: '#000',
            textShadow: '0 0 4px rgba(255, 255, 255, 1)',
            fontFamily: 'Arial, sans-serif',
            letterSpacing: '0.5px'
          }}
        >
          {nodeDatum.name}
        </text>
      </g>
    );
  };

  return (
    <PageContainer>
      <LeftPanel>
        <Title>Tree Visualizer</Title>
        
        <Controls>
          {/* Tree Operations */}
          <ControlGroup>
            <Label>Tree Operations</Label>
            <Input
              type="text"
              placeholder="Enter value to insert/delete"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && insertNode()}
            />
            <Button onClick={insertNode} disabled={isOperating}>Insert Node</Button>
            <Button onClick={deleteNode} disabled={isOperating} $variant="warning">Delete Node</Button>
            <Button onClick={generateRandomTree} disabled={isOperating}>Generate Random Tree</Button>
            <Button onClick={clearTree} disabled={isOperating} $variant="danger">Clear Tree</Button>
          </ControlGroup>
          
          {/* Search Operations */}
          <ControlGroup>
            <Label>Search Operations</Label>
            <Input
              type="text"
              placeholder="Enter value to search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchNode()}
            />
            <Button onClick={searchNode} disabled={isOperating}>Search Node</Button>
          </ControlGroup>
          
          {/* Traversal Operations */}
          <ControlGroup>
            <Label>Tree Traversals</Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <input
                type="checkbox"
                id="fastMode"
                checked={fastMode}
                onChange={(e) => setFastMode(e.target.checked)}
                style={{ margin: 0 }}
              />
              <label htmlFor="fastMode" style={{ color: 'white', fontSize: '0.9rem', margin: 0 }}>
                Fast Mode (Instant Traversal)
              </label>
            </div>
            <Button onClick={() => performTraversal('level')} disabled={isOperating}>Level Order</Button>
            <Button onClick={() => performTraversal('inorder')} disabled={isOperating}>In-Order</Button>
            <Button onClick={() => performTraversal('preorder')} disabled={isOperating}>Pre-Order</Button>
                      <Button onClick={() => performTraversal('postorder')} disabled={isOperating}>Post-Order</Button>
          <Button 
            onClick={() => {
              setVisitedNodes(new Set());
              setTraversalResult(null);
              addToLog('Traversal reset - cleared visited nodes');
            }} 
            disabled={isOperating}
            style={{ marginTop: '10px' }}
          >
            Reset Traversal
          </Button>
        </ControlGroup>
          
          {/* Animation Speed Control */}
          <ControlGroup>
            <Label>Animation Speed: {animationSpeed}ms</Label>
            <Input
              type="range"
              min="500"
              max="3000"
              value={animationSpeed}
              onChange={(e) => setAnimationSpeed(parseInt(e.target.value))}
            />
          </ControlGroup>
          
          {/* Stats */}
          <StatsContainer>
            <StatBox>
              <StatValue>{tree.size}</StatValue>
              <StatLabel>Nodes</StatLabel>
            </StatBox>
            <StatBox>
              <StatValue>{tree.getHeight()}</StatValue>
              <StatLabel>Height</StatLabel>
            </StatBox>
          </StatsContainer>
          
          {/* Operation Log */}
          <OperationLog>
            <LogTitle>Operation Log</LogTitle>
            {operationLog.map((entry, index) => (
              <LogEntry key={index}>{entry}</LogEntry>
            ))}
          </OperationLog>
        </Controls>
      </LeftPanel>
      
      <RightPanel>
        <VisualizationContainer>
          {treeData ? (
            <div style={{ width: '100%', height: '650px', display: 'flex', justifyContent: 'center' }}>
              <Tree
                data={treeData}
                renderCustomNodeElement={renderCustomNode}
                orientation="vertical"
                pathFunc="step"
                separation={{ siblings: 1.8, nonSiblings: 2.2 }}
                translate={{ x: 500, y: 100 }}
                nodeSize={{ x: 140, y: 120 }}
                zoom={0.75}
                enableLegends={false}
                pathClassFunc={() => 'tree-link'}
                style={{
                  links: {
                    stroke: 'white',
                    strokeWidth: 3,
                    fill: 'none'
                  }
                }}
              />
            </div>
          ) : (
            <div style={{ 
              color: 'rgba(255, 255, 255, 0.7)', 
              fontStyle: 'italic', 
              fontSize: '1.2rem',
              textAlign: 'center',
              padding: '50px'
            }}>
              Tree is empty
            </div>
          )}
          
          {/* Tree Info */}
          <TreeInfo>
            <InfoText><strong>Binary Tree:</strong> A hierarchical data structure where each node has at most two children</InfoText>
            <InfoText><strong>Operations:</strong> Insert, delete, search, and traverse nodes with visual feedback</InfoText>
            <InfoText><strong>Traversals:</strong> Level-order (BFS), In-order, Pre-order, and Post-order (DFS)</InfoText>
          </TreeInfo>
          
          {/* Traversal Result */}
          {traversalResult && (
            <TraversalResult>
              <TraversalTitle>{traversalResult.type.charAt(0).toUpperCase() + traversalResult.type.slice(1)} Traversal Result:</TraversalTitle>
              <TraversalPath>{traversalResult.path.join(' → ')}</TraversalPath>
            </TraversalResult>
          )}
        </VisualizationContainer>
      </RightPanel>
    </PageContainer>
  );
}

export default TreePage;
